import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, DatePicker, Input, message, Row, Select, Table } from "antd";

const { TextArea } = Input;
import { DeleteFilled, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { clampQtyRateValue } from "../../../utils/commonfunction";
import { PURCHASE_ORDER } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";
import ModalComponent from "../../../components/formComponent/ModalComponent";

const initialFormValues = {
  TransactionMasterId: null,
  TransactionNo: "",
  TransactionDate: moment().format("YYYY-MM-DD"),
  ReferenceNo: "",
  Description: "",
  TransactionTypeId: PURCHASE_ORDER,
  BranchId: null,
  DepartmentId: null,
  VendorId: null,
  // ParentTransactionMasterId: null,
  ParentTransactionMasterId: null,
  IsEnable: true,
  IsSubmit: false,
  TransactionDetails: [],
};

const CreateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [tableLoading, setTableLoading] = useState(false);
  const isEditMode = !!id;
  const [formFields, setFormFields] = useState(initialFormValues);
  const [formLoading, setFormLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [addDetailQty, setAddDetailQty] = useState("");
  const [addDetailRate, setAddDetailRate] = useState("");
  const itemSelectRef = useRef(null);
  const itemsFetchedRef = useRef(false);
  const [demandModalVisible, setDemandModalVisible] = useState(false);
  const [demandSearchText, setDemandSearchText] = useState("");
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchInitialData();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const branches = supportingTable?.Branches || [];
    const departments = supportingTable?.Departments || [];
    setFormFields((prev) => {
      let next = { ...prev };
      if (branches.length === 1 && (prev.BranchId == null || prev.BranchId === "")) {
        next.BranchId = branches[0].Id;
      }
      if (departments.length === 1 && (prev.DepartmentId == null || prev.DepartmentId === "")) {
        next.DepartmentId = departments[0].Id;
      }
      return next;
    });
  }, [supportingTable?.Branches, supportingTable?.Departments]);

  // Fetch items when both Branch and Department are selected
  useEffect(() => {
    if (formFields.BranchId && formFields.DepartmentId && supportingTable?.Branches?.length > 0) {
      // Only fetch if items haven't been fetched yet or if branch/department changed
      const currentKey = `${formFields.BranchId}-${formFields.DepartmentId}`;
      if (!itemsFetchedRef.current || itemsFetchedRef.current !== currentKey) {
        fetchItems();
        itemsFetchedRef.current = currentKey;
      }
    } else {
      // Reset items when branch or department is cleared
      setSupportingTable((prev) => ({
        ...prev,
        Items: [],
      }));
      itemsFetchedRef.current = null;
    }
  }, [formFields.BranchId, formFields.DepartmentId, supportingTable?.Branches]);

  // Fetch demands when vendor is selected
  useEffect(() => {
    if (formFields.VendorId) {
      fetchDemandsByVendor(formFields.VendorId);
    } else {
      setSupportingTable((prev) => ({
        ...prev,
        Demands: [],
      }));
    }
  }, [formFields.VendorId]);

  // In edit mode, fetch demands when both BranchId and VendorId are available
  useEffect(() => {
    if (isEditMode && formFields.BranchId && formFields.VendorId) {
      fetchDemandsByVendor(formFields.VendorId);
    }
  }, [isEditMode, formFields.BranchId, formFields.VendorId]);

  const fetchInitialData = async () => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: PURCHASE_ORDER,
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      IsDetailData: isEditMode ? true : false,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }

      const dataSet = response.data;

      if (isEditMode) {

        const masterData = dataSet?.Data?.[0] || {};
        setFormFields((prev) => ({ ...prev, ...masterData }));

        const detailsData = masterData?.TransactionDetails || [];
        setTransactionDetails(detailsData);
      }

      // Set supporting tables - only Branches, Departments, Vendors, and TransactionNo initially
      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || dataSet.DataSet?.Table1 || [],
          Departments: dataSet.AdditionalData?.Departments || dataSet.DataSet?.Table2 || [],
          Vendors: dataSet.AdditionalData?.Vendors || [],
          TransactionNo: dataSet?.AdditionalData?.TransactionCode?.[0]?.Code || "",
          Items: [], // Don't load items initially
          Demands: [],
        });
      }

    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };

  const fetchItems = async () => {
    if (!formFields.BranchId || !formFields.DepartmentId) {
      return;
    }

    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: PURCHASE_ORDER,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching items"
        );
        return;
      }

      const dataSet = response.data;

      // Update Items in supporting table
      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable((prev) => ({
          ...prev,
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || [],
        }));
      }

    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching items:", error);
        message.error("Failed to fetch items");
      }
    }
  };

  const fetchTransactionRelatedData = async (id, dataType, options = {}) => {
    const { showErrorMessage = true, onClear, onSuccess } = options;

    if (!id) {
      if (onClear) onClear();
      return;
    }

    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      Id: id,
      DataType: dataType,
      BranchId: formFields.BranchId || null,
      DepartmentId: formFields.DepartmentId || null,
    };

    try {
      const response = await postRequest("/Inv_TransactionRelatedData", payload, controller);

      if (response.error) {
        const errorMsg = response.errorMessage || `Error fetching ${dataType}`;
        console.error(`Error fetching ${dataType}:`, errorMsg);
        if (showErrorMessage) {
          message.error(errorMsg);
        }
        if (onClear) onClear();
        return;
      }

      if (response.data.response === false) {
        const errorMsg = response.data.DataSet?.Table?.errorMessage || `Error fetching ${dataType}`;
        console.error(`Error fetching ${dataType}:`, errorMsg);
        if (showErrorMessage) {
          message.error(errorMsg);
        }
        if (onClear) onClear();
        return;
      }

      const dataSet = response.data;
      const data = dataSet.Data || dataSet.DataSet?.Table || [];

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      if (error.message !== "canceled") {
        console.error(`Error fetching ${dataType}:`, error);
        if (showErrorMessage) {
          message.error(`Failed to fetch ${dataType}`);
        }
      }
      if (onClear) onClear();
    }
  };

  const fetchDemandsByVendor = async (vendorId) => {
    await fetchTransactionRelatedData(
      vendorId,
      "GetPendingDemandsForPO",
      {
        showErrorMessage: false,
        onClear: () => {
          setSupportingTable((prev) => ({
            ...prev,
            Demands: [],
          }));
        },
        onSuccess: (demands) => {
          setSupportingTable((prev) => ({
            ...prev,
            Demands: demands,
          }));
        },
      }
    );
  };

  const fetchDemandItemsForPO = async (demandId) => {
    await fetchTransactionRelatedData(
      demandId,
      "GetDemandItemsForPO",
      {
        showErrorMessage: true,
        onClear: () => {
          setTransactionDetails([]);
        },
        onSuccess: (items) => {
          const mappedDetails = items.map((item) => {
            // const remainingQty = (item.QtyPU ?? item.QtyIU ?? 0) - (item.OrderedQtyPU ?? item.IssuedQtyIU ?? 0);
            const remainingQty = (item.QtyPU ?? item.QtyIU ?? 0) - (item.DeliveredQtyPU ?? 0);
            const demandRate = item.LastPurchaseRatePU ?? item.LastPurchaseRateIU ?? item.RatePU ?? item.Rate ?? 0;

            return {
              ItemId: item.ItemId ?? item.Id,
              ItemName: item.ItemName ?? item.Name ?? "",
              PurchaseUnitId: item.PurchaseUnitId ?? item.UnitId ?? null,
              PurchaseUnit: item.PurchaseUnit ?? item.UnitName ?? "",
              QtyPU: remainingQty,
              RatePU: demandRate,
              IssuanceUnitId: item.IssuanceUnitId ?? null,
              IssuanceUnit: item.IssuanceUnit ?? "",
              QtyIU: item.QtyIU ?? 0,
              RateIU: item.RateIU ?? 0,
              RecipeUnitId: item.RecipeUnitId ?? null,
              QtyRU: item.QtyRU ?? 0,
              RateRU: item.RateRU ?? 0,
              StockIU: item.StockIU ?? 0,
              StockPU: item.StockPU ?? 0,
              IsEnable: item.IsEnable !== false,
              LastPurchaseRatePU: demandRate,
            };
          });

          setTransactionDetails(mappedDetails);
        },
      }
    );
  };

  const handleFormChange = (data) => {
    setFormFields((prev) => {
      const updated = { ...prev, [data.name]: data.value };

      // If Branch or Department is cleared, clear items
      if (data.name === "BranchId" || data.name === "DepartmentId") {
        if (!updated.BranchId || !updated.DepartmentId) {
          setSupportingTable((prev) => ({
            ...prev,
            Items: [],
          }));
          setSelectedItemForAdd(null);
          itemsFetchedRef.current = null;
        }
      }

      // When Vendor is selected or changed, fetch Demands for that vendor
      if (data.name === "VendorId") {
        if (data.value) {
          fetchDemandsByVendor(data.value);
        } else {
          setSupportingTable((prev) => ({
            ...prev,
            Demands: [],
          }));
        }
        updated.ParentTransactionMasterId = null;
        setTransactionDetails([]);
      }

      // When Demand is selected or changed, fetch Demand Items
      if (data.name === "ParentTransactionMasterId") {
        if (data.value) {
          if (!isEditMode) {
            fetchDemandItemsForPO(data.value);
          }
        } else {
          setTransactionDetails([]);
        }
      }

      return updated;
    });
  };

  const handleDateChange = (date) => {
    setFormFields((prev) => ({
      ...prev,
      TransactionDate: date ? date.format("YYYY-MM-DD") : null,
    }));
  };

  const itemList = supportingTable?.Items || [];
  const vendors = supportingTable?.Vendors || [];
  const demands = supportingTable?.Demands || [];

  const handleAddDetail = () => {
    const qty = clampQtyRateValue(addDetailQty);
    const rate = clampQtyRateValue(addDetailRate);

    if (selectedItemForAdd) {
      const itemId = selectedItemForAdd.ItemId ?? selectedItemForAdd.Id;
      const isDuplicate = transactionDetails.some(
        (d) => (d.ItemId ?? d.Id) != null && (d.ItemId ?? d.Id) === itemId
      );
      if (isDuplicate) {
        message.error("This item is already added to the table. Duplicate items are not allowed.");
        return;
      }

      const newDetail = {
        ItemId: itemId,
        ItemName: selectedItemForAdd.ItemName ?? selectedItemForAdd.Name,
        PurchaseUnitId: selectedItemForAdd.PurchaseUnitId ?? selectedItemForAdd.UnitId ?? null,
        PurchaseUnit: selectedItemForAdd.PurchaseUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyPU: qty,
        RatePU: rate,
        IssuanceUnitId: selectedItemForAdd.IssuanceUnitId ?? selectedItemForAdd.UnitId ?? null,
        IssuanceUnit: selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyIU: 0,
        RateIU: 0,
        RecipeUnitId: null,
        QtyRU: 0,
        RateRU: 0,
        StockIU: selectedItemForAdd.StockIU ?? 0,
        StockPU: selectedItemForAdd.StockPU ?? 0,
        IsEnable: true,
        LastPurchaseRatePU: selectedItemForAdd.LastPurchaseRatePU ?? 0,
      };
      setTransactionDetails([newDetail, ...transactionDetails]);
      setSelectedItemForAdd(null);
      setAddDetailQty("");
      setAddDetailRate("");
      setTimeout(() => itemSelectRef.current?.focus(), 0);
    }
  };

  const handleDetailChange = (index, field, value) => {
    if (field === "QtyPU" || field === "RatePU") {
      value = clampQtyRateValue(value);
    }
    const updatedDetails = [...transactionDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };

    // Calculate totals if needed
    if (field === "QtyPU" || field === "RatePU") {
      updatedDetails[index].QtyPU = updatedDetails[index].QtyPU || 0;
      updatedDetails[index].RatePU = updatedDetails[index].RatePU || 0;
    }

    setTransactionDetails(updatedDetails);
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = transactionDetails.filter((_, i) => i !== index);
    setTransactionDetails(updatedDetails);
  };

  const handleFormSave = async (e, operationId, isSubmitFlag) => {
    e.preventDefault();

    if (!formFields.VendorId) {
      message.error("Please select a vendor");
      return;
    }

    if (!formFields.BranchId) {
      message.error("Please select a branch");
      return;
    }

    if (!formFields.DepartmentId) {
      message.error("Please select a department");
      return;
    }

    if (transactionDetails.length === 0) {
      message.error("Please add at least one transaction detail");
      return;
    }

    setFormLoading(true);

    const payload = {
      OperationId: operationId, // 2 for create, 3 for update
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      TransactionNo: formFields.TransactionNo,
      TransactionDate: formFields.TransactionDate,
      ReferenceNo: formFields.ReferenceNo,
      Description: formFields.Description ?? "",
      TransactionTypeId: PURCHASE_ORDER,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
      Gross: 0,
      Tax: 0,
      Discount: 0,
      Amount: 0,
      VendorId: formFields.VendorId || null,
      // ParentTransactionMasterId: formFields.ParentTransactionMasterId || null,
      ParentTransactionMasterId: formFields.ParentTransactionMasterId || null,
      IsEnable: formFields.IsEnable,
      IsSubmit: isSubmitFlag || formFields.IsSubmit,
      TransactionDetails: transactionDetails.map((detail) => ({
        ItemId: detail.ItemId,
        PurchaseUnitId: detail.PurchaseUnitId || null,
        QtyPU: detail.QtyPU || 0,
        RatePU: detail.RatePU || 0,
        IssuanceUnitId: detail.IssuanceUnitId || null,
        QtyIU: 0,
        RateIU: 0,
        RecipeUnitId: detail.RecipeUnitId || null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: detail.IsEnable !== false,
      })),
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setFormLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          `Error ${isEditMode ? "updating" : "creating"} transaction`
        );
        return;
      }

      message.success(
        response.successMessage ||
        `Transaction ${isEditMode ? "updated" : "created"} successfully`
      );
      navigate("/purchaseOrder");
    } catch (error) {
      setFormLoading(false);
      if (error.message !== "canceled") {
        console.error("Error saving transaction:", error);
        const apiError =
          error.response?.data?.Error_Message ||
          error.response?.data?.Message ||
          error.response?.data?.errorMessage ||
          error.response?.data?.ResponseMessage;
        message.error(
          apiError || `Failed to ${isEditMode ? "update" : "create"} purchase order`
        );
      }
    }
  };

  const detailColumns = useMemo(
    () => [
      { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
      { title: "Purchase Unit", dataIndex: "PurchaseUnit", key: "PurchaseUnit" },
      { title: "Stock Quantity", dataIndex: "StockPU", key: "StockPU" },
      { title: "Last Purchase Rate", dataIndex: "LastPurchaseRatePU", key: "LastPurchaseRatePU" },
      {
        title: "Qty",
        dataIndex: "QtyPU",
        key: "QtyPU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            min={0}
            value={record.QtyPU}
            onChange={(e) =>
              handleDetailChange(index, "QtyPU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      {
        title: "Rate",
        dataIndex: "RatePU",
        key: "RatePU",
        width: 100,
        render: (_, record, index) => (
          <Input
            type="number"
            min={0}
            value={record.RatePU}
            onChange={(e) =>
              handleDetailChange(index, "RatePU", parseFloat(e.target.value) || 0)
            }
            style={{ width: "100%" }}
            readOnly={formFields.IsSubmit}
          />
        ),
      },
      {
        title: "Amount",
        key: "Amount",
        width: 120,
        align: "right",
        render: (_, record) => {
          const qty = record.QtyPU ?? 0;
          const rate = record.RatePU ?? 0;
          const amount = (parseFloat(qty) || 0) * (parseFloat(rate) || 0);
          return amount.toFixed(2);
        },
      },
      ...(!formFields.IsSubmit
        ? [
          {
            title: "Action",
            key: "actions",
            width: 100,
            render: (_, record, index) => (
              <Button
                type="link"
                danger
                icon={<DeleteFilled />}
                onClick={() => handleRemoveDetail(index)}
              >
              </Button>
            ),
          },
        ]
        : []),
    ],
    [formFields.IsSubmit, handleDetailChange, handleRemoveDetail]
  );

  const labelStyle = { display: "block", marginBottom: 6, fontWeight: 500 };

  const formPanel = (
    <Fragment>

      <Col xs={24} sm={12} md={4}>
        <FormTextField
          colSpan={24}
          label="Transaction No"
          name="TransactionNo"
          size={INPUT_SIZE}
          value={isEditMode ? formFields.TransactionNo : supportingTable?.TransactionNo}
          readOnly={true}
        />
      </Col>
      <Col xs={24} sm={12} md={4}>
        <label style={labelStyle}>
          Transaction Date <span style={{ color: "#ff4d4f" }}>*</span>
        </label>
        {!formFields.IsSubmit ? (
          <DatePicker
            style={{ width: "100%" }}
            value={formFields.TransactionDate ? moment(formFields.TransactionDate) : null}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
          />
        ) : (
          <FormTextField
            name="TransactionDate"
            size={INPUT_SIZE}
            value={formFields.TransactionDate ? moment(formFields.TransactionDate).format("YYYY-MM-DD") : null}
            readOnly={true}
          />
        )}
      </Col>
      <Col xs={24} sm={12} md={4}>
        <FormSelect
          colSpan={24}
          listItem={supportingTable.Branches || []}
          idName="Id"
          valueName="Name"
          size={INPUT_SIZE}
          name="BranchId"
          label="Branch"
          value={formFields.BranchId}
          onChange={handleFormChange}
          required={true}
          disabled={formFields.IsSubmit}
        />
      </Col>
      <Col xs={24} sm={12} md={4}>
        <FormSelect
          colSpan={24}
          listItem={supportingTable.Departments || []}
          idName="Id"
          valueName="Name"
          size={INPUT_SIZE}
          name="DepartmentId"
          label="Department"
          value={formFields.DepartmentId}
          onChange={handleFormChange}
          required={true}
          disabled={formFields.IsSubmit}
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <FormSelect
          colSpan={24}
          listItem={vendors || []}
          idName="Id"
          valueName="Name"
          size={INPUT_SIZE}
          name="VendorId"
          label="Vendor"
          value={formFields.VendorId}
          onChange={handleFormChange}
          required={true}
          disabled={formFields.IsSubmit}
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Row gutter={8} align="bottom">
          <Col span={20}>
            <FormSelect
              colSpan={24}
              listItem={demands}
              idName="Id"
              valueName="Name"
              size={INPUT_SIZE}
              name="ParentTransactionMasterId"
              label="Demand"
              value={formFields.ParentTransactionMasterId}
              onChange={handleFormChange}
              disabled={formFields.IsSubmit || !formFields.VendorId}
              allowClear={false}
              placeholder={!formFields.VendorId ? "Select vendor first" : "Select demand"}
            />
          </Col>
          <Col span={4}>
            <Button
              type="link"
              onClick={() => {
                fetchDemandsByVendor(formFields.VendorId || null);
                setDemandModalVisible(true);
              }}
              disabled={formFields.IsSubmit || !formFields.VendorId}
              style={{ padding: "4px 0", marginBottom: 8 }}
            >
              Search
            </Button>
          </Col>
        </Row>
      </Col>
      <ModalComponent
        title="Select Demand"
        isModalVisible={demandModalVisible}
        handleCancel={() => {
          setDemandModalVisible(false);
          setDemandSearchText("");
        }}
        footer={null}
        width={700}
        closable={true}
      >
        <Input
          placeholder="Search by date, transaction no, or vendor..."
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
          allowClear
          value={demandSearchText}
          onChange={(e) => setDemandSearchText(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <Table
          dataSource={
            (supportingTable?.Demands || []).filter((record) => {
              if (!demandSearchText.trim()) return true;
              const search = demandSearchText.toLowerCase().trim();
              const date = (record.Date ?? "").toString().toLowerCase();
              const name = (record.Name ?? "").toString().toLowerCase();
              const vendor = (record.VendorName ?? "").toString().toLowerCase();
              return (
                date.includes(search) ||
                name.includes(search) ||
                vendor.includes(search)
              );
            })
          }
          rowKey={(record) => record.Id ?? record.id ?? record.TransactionMasterId ?? Math.random()}
          pagination={{ pageSize: 10 }}
          size="small"
          onRow={(record) => ({
            onClick: (e) => {
              if (e.target.closest("button")) return;
              const demandId = record.Id ?? record.id ?? record.ParentTransactionMasterId ?? record.TransactionMasterId;
              if (demandId) {
                setFormFields((prev) => ({
                  ...prev,
                  ParentTransactionMasterId: demandId,
                }));
                fetchDemandItemsForPO(demandId);
                setDemandModalVisible(false);
                setDemandSearchText("");
              }
            },
            style: { cursor: "pointer" },
          })}
          columns={[
            { title: "Date", dataIndex: "Date", key: "Date", width: 120 },
            { title: "Transaction No", dataIndex: "Name", key: "Name", width: 150 },

          ]}
        />
      </ModalComponent>
      <Col span={24}>
        <label style={labelStyle}>Description</label>
        <TextArea
          name="Description"
          value={formFields.Description ?? ""}
          onChange={(e) => handleFormChange({ name: "Description", value: e.target.value })}
          rows={1}
          placeholder="Enter description (optional)"
          readOnly={formFields.IsSubmit}
          disabled={formFields.IsSubmit}
          style={{ width: "100%" }}
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <FormTextField
          colSpan={24}
          label="Total Amount"
          name="TotalAmount"
          size={INPUT_SIZE}
          value={transactionDetails.reduce((sum, detail) => {
            const qty = detail.QtyPU ?? 0;
            const rate = detail.RatePU ?? 0;
            return sum + ((parseFloat(qty) || 0) * (parseFloat(rate) || 0));
          }, 0).toFixed(2)}
          readOnly={true}
          type="text"
          isNumber="true"
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <FormTextField
          colSpan={24}
          label="Total Items"
          name="TotalItems"
          size={INPUT_SIZE}
          value={transactionDetails.length}
          readOnly={true}
          type="text"
          isNumber="true"
        />
      </Col>

    </Fragment>
  );

  const getItemId = (item) => item?.ItemId ?? item?.Id;
  const getItemName = (item) => item?.ItemName + " ( " + (item?.PurchaseUnit ?? item?.UnitName ?? "") + " )";
  const getItemCode = (item) => item?.ItemCode ?? item?.Code ?? "";
  const getBarcode = (item) => item?.Barcode ?? item?.BarCode ?? "";
  const getStockPU = (item) => item?.StockPU ?? item?.Stock ?? 0;
  const getLastPurchaseRate = (item) => item?.LastPurchaseRatePU ?? 0;

  const addDetailEntryPanel = (
    <>
      {!formFields.IsSubmit && (
        <div
          style={{
            width: "100%",
            padding: "16px",
            background: "#fafafa",
            borderRadius: 8,
            border: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              marginBottom: 12,
              fontSize: 14,
              fontWeight: 600,
              color: "#262626",
            }}
          >
            Add item to purchase order
          </div>
          <Row gutter={[16, 12]} align="bottom">
            <Col xs={24} sm={10} md={4} lg={3}>
              <label style={labelStyle}>Bar code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Bar code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getBarcode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getBarcode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getBarcode(i))
                    .map((item) => ({
                      value: getBarcode(item),
                      label: getBarcode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={10} md={4} lg={3}>
              <label style={labelStyle}>Item code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Item code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemCode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemCode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getItemCode(i))
                    .map((item) => ({
                      value: getItemCode(item),
                      label: getItemCode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Item name</label>
              <Select
                ref={itemSelectRef}
                style={{ width: "100%" }}
                placeholder={
                  !formFields.BranchId || !formFields.DepartmentId
                    ? "Select Branch and Department first"
                    : "Search by item name"
                }
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemId(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemId(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList.map((item) => ({
                    value: getItemId(item),
                    label: getItemName(item),
                  })),
                ]}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Available Stock</label>
              <Input
                readOnly
                disabled
                size="middle"
                value={selectedItemForAdd ? getStockPU(selectedItemForAdd).toFixed(2) : "—"}
                style={{
                  background: "#fff",
                  cursor: "default",
                  color: "#595959",
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Last Purchase Rate </label>
              <Input
                readOnly
                disabled
                size="middle"
                value={selectedItemForAdd ? getLastPurchaseRate(selectedItemForAdd).toFixed(2) : "—"}
                style={{
                  background: "#fff",
                  cursor: "default",
                  color: "#595959",
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Quantity</label>
              <Input
                type="number"
                placeholder="0"
                size="middle"
                value={addDetailQty}
                onChange={(e) => setAddDetailQty(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setAddDetailRate("");
                    setTimeout(() => {
                      const rateInput = document.querySelector('input[placeholder="Rate"]');
                      rateInput?.focus();
                    }, 0);
                  }
                }}
                min={0}
                step={1}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Rate</label>
              <Input
                type="number"
                placeholder="Rate"
                size="middle"
                value={addDetailRate}
                onChange={(e) => setAddDetailRate(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddDetail();
                }}
                min={0}
                step={0.01}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col>
          </Row>
        </div>
      )}
    </>
  );

  return (
    <BasicFormComponentInvTransaction
      pageMode="transaction"
      isEditMode={isEditMode}
      formTitle={"Purchase Order" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      crudTitle={"Purchase Order" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      baseRoute="/purchaseOrder"
      formPanel={formPanel}
      transactionDetailsColumns={detailColumns}
      transactionDetailsRows={transactionDetails}
      onAddDetail={handleAddDetail}
      onFormSave={handleFormSave}
      showSubmit={!formFields.IsSubmit && checkFeatureAuth("POST", "PurchaseOrder", { showErrorMessage: false })}
      formLoading={formLoading}
      submitFormLoading={formLoading}
      onCancelForm={() => navigate("/purchaseOrder")}
      addDetailEntryPanel={addDetailEntryPanel}
      showSave={!formFields.IsSubmit}
      showAddDetailButton={!formFields.IsSubmit}
    />
  );
};

export default CreateEdit;
